import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Add,
  Delete,
  Search,
  Close,
  LinkOutlined,
  Visibility,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Tooltip,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useUser } from '../../hooks/useUser';
import { auth } from '../../sp-firebase/firebase';
import { COLOR_DICTIONARY } from '../../sp-types/colors';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:8080';

// ── Types ────────────────────────────────────────────────────

interface Account { id: string; name: string }

interface Formula {
  id: number;
  code: string;
  name: string;
  unit: string;
  contractType: string | null;
}

interface DerivedLine {
  formulaCode: string;
  formulaName: string;
  allocationPercent: number;
  priceRatio: number;
  linkedRatioCode: string | null;
}

interface BaseTemplate {
  id: number;
  baseFormulaId: number;
  formulaCode: string;
  name: string;
  derived: DerivedLine[];
}

interface AccountFormula {
  id: number;
  accountId: number;
  formulaId: number;
  formulaCode: string;
  formulaName: string;
  unit: string;
  contractType: string | null;
  isFavorite: boolean;
  clientCode: string | null;
  isPriceWatchOnly: boolean;
  // Hedge derivation info
  isBaseMaterial: boolean;
  baseTemplateId: number | null;
  derivedLines: DerivedLine[];
  // If this formula IS a derived material
  derivedFromBase: string | null; // base formula code
  allocationPercent: number | null;
  priceRatio: number | null;
  linkedRatioCode: string | null;
}

// ── API helpers ──────────────────────────────────────────────

async function getHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

async function fetchAccounts(): Promise<Account[]> {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/accounts`, { headers });
  if (!res.ok) throw new Error('Failed to fetch accounts');
  const data = await res.json();
  return data.accounts || [];
}

async function fetchAllFormulas(): Promise<Formula[]> {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/formulas`, { headers });
  if (!res.ok) throw new Error('Failed to fetch formulas');
  const data = await res.json();
  return data.formulas || [];
}

async function fetchAccountFormulasRaw(accountId: string): Promise<any[]> {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/accounts/${accountId}/formulas`, { headers });
  if (!res.ok) throw new Error('Failed to fetch account formulas');
  const data = await res.json();
  return data.formulas || [];
}

async function fetchBaseTemplates(accountId: string): Promise<BaseTemplate[]> {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/base-hedge-materials/account/${accountId}`, { headers });
  if (!res.ok) return [];
  const templates: any[] = await res.json();

  // Fetch derived lines for each template
  const result: BaseTemplate[] = [];
  for (const t of templates) {
    let derived: DerivedLine[] = [];
    try {
      const dRes = await fetch(`${API_URL}/api/v2/base-hedge-materials/${t.id}/derived`, { headers });
      if (dRes.ok) {
        const lines: any[] = await dRes.json();
        derived = lines.map(l => ({
          formulaCode: l.formula_code,
          formulaName: l.formula_name,
          allocationPercent: Number(l.allocation_percent) || 0,
          priceRatio: Number(l.price_ratio) || 0,
          linkedRatioCode: l.linked_ratio_code || null,
        }));
      }
    } catch { /* ignore */ }
    result.push({
      id: t.id,
      baseFormulaId: t.base_formula_id,
      formulaCode: t.formula_code,
      name: t.name,
      derived,
    });
  }
  return result;
}

async function addFormulaToAccount(accountId: string, formulaId: number, isPriceWatchOnly: boolean) {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/accounts/${accountId}/formulas`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ formulaId, isPriceWatchOnly }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to add formula');
  }
  return res.json();
}

async function removeFormulaFromAccount(accountId: string, formulaId: number) {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/accounts/${accountId}/formulas/${formulaId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove formula');
  }
  return res.json();
}

async function updateFormulaSettings(accountId: string, formulaId: number, settings: Record<string, any>) {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/accounts/${accountId}/formulas/${formulaId}`, {
    method: 'PATCH',
    headers,
    body: JSON.stringify({ settings }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update settings');
  }
  return res.json();
}

async function createBaseTemplate(accountId: string, baseFormulaId: number) {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/base-hedge-materials/account/${accountId}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ baseFormulaId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to set as base material');
  }
  return res.json();
}

async function deleteBaseTemplate(templateId: number) {
  const headers = await getHeaders();
  const res = await fetch(`${API_URL}/api/v2/base-hedge-materials/${templateId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to remove base material');
  }
  return res.json();
}

// ── Component ────────────────────────────────────────────────

const MaterialsPerAccount: React.FC = () => {
  const { data: userData } = useUser();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [accountFormulas, setAccountFormulas] = useState<AccountFormula[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Add dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [allFormulas, setAllFormulas] = useState<Formula[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [addAsPriceWatch, setAddAsPriceWatch] = useState(false);

  // Load accounts
  useEffect(() => {
    fetchAccounts().then(setAccounts).catch(console.error);
  }, []);

  // Auto-select account if user has one
  useEffect(() => {
    if (!selectedAccountId && userData?.accountId) {
      setSelectedAccountId(userData.accountId);
    }
  }, [userData, selectedAccountId]);

  // Load account formulas + base hedge templates
  const loadAccountData = useCallback(async () => {
    if (!selectedAccountId) return;
    setLoading(true);
    try {
      const [rawFormulas, templates] = await Promise.all([
        fetchAccountFormulasRaw(selectedAccountId),
        fetchBaseTemplates(selectedAccountId),
      ]);

      // Build lookup maps from templates
      const baseByFormulaId = new Map<number, BaseTemplate>();
      const derivedMap = new Map<string, { baseCode: string; alloc: number; ratio: number; linkedRatio: string | null }>();

      for (const t of templates) {
        baseByFormulaId.set(t.baseFormulaId, t);
        for (const d of t.derived) {
          derivedMap.set(d.formulaCode, {
            baseCode: t.formulaCode,
            alloc: d.allocationPercent,
            ratio: d.priceRatio,
            linkedRatio: d.linkedRatioCode,
          });
        }
      }

      const enriched: AccountFormula[] = rawFormulas.map((f: any) => {
        const baseT = baseByFormulaId.get(f.formulaId);
        const derivedInfo = derivedMap.get(f.formulaCode);
        const isPWO = f.isPriceWatchOnly === true;

        return {
          id: f.id,
          accountId: f.accountId,
          formulaId: f.formulaId,
          formulaCode: f.formulaCode,
          formulaName: f.formulaName,
          unit: f.unit,
          contractType: f.contractType,
          isFavorite: f.isFavorite,
          clientCode: f.clientCode,
          isPriceWatchOnly: isPWO,
          isBaseMaterial: !!baseT,
          baseTemplateId: baseT?.id ?? null,
          derivedLines: baseT?.derived ?? [],
          derivedFromBase: derivedInfo?.baseCode ?? null,
          allocationPercent: derivedInfo?.alloc ?? null,
          priceRatio: derivedInfo?.ratio ?? null,
          linkedRatioCode: derivedInfo?.linkedRatio ?? null,
        };
      });

      setAccountFormulas(enriched);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId]);

  useEffect(() => {
    loadAccountData();
  }, [loadAccountData]);

  // Assigned formula IDs for filtering the add dialog
  const assignedFormulaIds = useMemo(
    () => new Set(accountFormulas.map(f => f.formulaId)),
    [accountFormulas],
  );

  // Color map — matches the auto-generated dictionary used elsewhere
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    accountFormulas.forEach((f, i) => {
      map[f.formulaCode] = COLOR_DICTIONARY[i % COLOR_DICTIONARY.length];
    });
    return map;
  }, [accountFormulas]);

  // ── Handlers ──

  const handleOpenAddDialog = async () => {
    setSearchTerm('');
    setAddAsPriceWatch(false);
    try {
      const formulas = await fetchAllFormulas();
      setAllFormulas(formulas);
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
      return;
    }
    setShowAddDialog(true);
  };

  const handleAddFormula = async (formula: Formula) => {
    try {
      await addFormulaToAccount(selectedAccountId, formula.id, addAsPriceWatch);
      setMessage({ type: 'success', text: `Added ${formula.code} (${formula.name})` });
      await loadAccountData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleRemoveFormula = async (af: AccountFormula) => {
    if (!window.confirm(`Remove ${af.formulaCode} (${af.formulaName}) from this account?`)) return;
    try {
      await removeFormulaFromAccount(selectedAccountId, af.formulaId);
      setMessage({ type: 'success', text: `Removed ${af.formulaCode}` });
      await loadAccountData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleTogglePriceWatch = async (af: AccountFormula) => {
    const newVal = !af.isPriceWatchOnly;
    try {
      await updateFormulaSettings(selectedAccountId, af.formulaId, { isPriceWatchOnly: newVal });
      setAccountFormulas(prev =>
        prev.map(f => f.id === af.id ? { ...f, isPriceWatchOnly: newVal } : f),
      );
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleToggleBase = async (af: AccountFormula) => {
    try {
      if (af.isBaseMaterial && af.baseTemplateId) {
        // Remove base template
        if (af.derivedLines.length > 0) {
          if (!window.confirm(`This will also remove ${af.derivedLines.length} derived material config(s). Continue?`)) return;
        }
        await deleteBaseTemplate(af.baseTemplateId);
        setMessage({ type: 'success', text: `${af.formulaCode} is no longer a base hedge material` });
      } else {
        // Create base template
        await createBaseTemplate(selectedAccountId, af.formulaId);
        setMessage({ type: 'success', text: `${af.formulaCode} set as base hedge material` });
      }
      await loadAccountData();
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  // ── Filtered formulas for add dialog ──

  const availableFormulas = useMemo(() => {
    let list = allFormulas.filter(f => !assignedFormulaIds.has(f.id));
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(f =>
        f.code.toLowerCase().includes(q) || f.name.toLowerCase().includes(q),
      );
    }
    return list;
  }, [allFormulas, assignedFormulaIds, searchTerm]);

  // ── Render helpers ──

  const contractTypeBadge = (ct: string | null) => {
    if (!ct) return null;
    const colors: Record<string, string> = {
      material: 'bg-blue-100 text-blue-700',
      ratio: 'bg-purple-100 text-purple-700',
      FX: 'bg-amber-100 text-amber-700',
    };
    return (
      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${colors[ct] || 'bg-gray-100 text-gray-600'}`}>
        {ct}
      </span>
    );
  };

  const hedgeRoleCell = (af: AccountFormula) => {
    if (af.isBaseMaterial) {
      return (
        <div className="flex flex-col gap-0.5">
          <span className="text-[11px] font-medium text-blue-700">Base hedge</span>
          {af.derivedLines.length > 0 && (
            <div className="text-[10px] text-gray-400">
              → {af.derivedLines.map(d => d.formulaCode).join(', ')}
            </div>
          )}
        </div>
      );
    }
    if (af.derivedFromBase) {
      return (
        <div className="flex items-center gap-1.5">
          <LinkOutlined className="w-3.5 h-3.5 text-purple-400" />
          <div className="text-[11px]">
            <span className="font-mono font-semibold text-purple-700">{af.derivedFromBase}</span>
            {af.linkedRatioCode ? (
              <span className="text-gray-500 ml-1">× <span className="font-mono text-purple-600">{af.linkedRatioCode}</span></span>
            ) : af.priceRatio != null && af.priceRatio !== 0 ? (
              <span className="text-gray-500 ml-1">× {af.priceRatio}</span>
            ) : null}
          </div>
        </div>
      );
    }
    return <span className="text-xs text-gray-300">—</span>;
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 text-left">Materials per Account</h1>
          <p className="text-sm text-gray-500 mt-1">
            Assign materials, configure visibility and hedge derivation
          </p>
        </div>
        <button
          onClick={handleOpenAddDialog}
          disabled={!selectedAccountId}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Add className="w-5 h-5" />
          Add Material
        </button>
      </div>

      {/* Account selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
        <Select
          value={selectedAccountId}
          onChange={e => setSelectedAccountId(e.target.value as string)}
          displayEmpty
          size="small"
          className="!max-w-sm"
          fullWidth
          renderValue={(val) => {
            if (!val) return <span className="text-gray-400">Select account...</span>;
            const acc = accounts.find(a => String(a.id) === val);
            return acc ? (
              <span>{acc.name} <span className="text-xs text-gray-400 ml-1">#{acc.id}</span></span>
            ) : val;
          }}
        >
          <MenuItem value=""><span className="text-gray-400">Select account...</span></MenuItem>
          {accounts.map(a => (
            <MenuItem key={a.id} value={String(a.id)}>
              <span>{a.name}</span>
              <span className="text-xs text-gray-400 ml-2">#{a.id}</span>
            </MenuItem>
          ))}
        </Select>
      </div>

      {/* Message banner */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm flex items-center justify-between ${
            message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}
        >
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} className="ml-2 hover:opacity-70">
            <Close className="w-4 h-4" />
          </button>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        </div>
      )}

      {!loading && selectedAccountId && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-3 py-3 w-8"></th>
                <th className="px-3 py-3">Code</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">Unit</th>
                <th className="px-3 py-3">Type</th>
                <th className="px-3 py-3">Hedge Role</th>
                <th className="px-3 py-3 text-center w-20">Base</th>
                <th className="px-3 py-3 text-center w-28">Watch Only</th>
                <th className="px-3 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accountFormulas.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-sm text-gray-400">No materials assigned</td></tr>
              ) : (
                accountFormulas.map(af => {
                  const isPW = af.isPriceWatchOnly;
                  return (
                    <tr
                      key={af.id}
                      className={`transition-colors ${isPW ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'}`}
                    >
                      {/* Color swatch */}
                      <td className="px-3 py-2.5">
                        <div
                          className="w-4 h-4 rounded-full border border-gray-200"
                          style={{ backgroundColor: colorMap[af.formulaCode] || '#ccc' }}
                        />
                      </td>
                      {/* Code */}
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono text-sm font-semibold text-gray-900">
                            {af.formulaCode}
                          </span>
                          {isPW && (
                            <Tooltip title="Watch only (historical prices)">
                              <Visibility className="!w-3 !h-3 text-gray-400" />
                            </Tooltip>
                          )}
                        </div>
                      </td>
                      {/* Name */}
                      <td className="px-3 py-2.5 text-sm text-gray-700">
                        {af.formulaName}
                      </td>
                      {/* Unit */}
                      <td className="px-3 py-2.5 text-sm text-gray-500">
                        {af.unit && af.unit !== 'unit' ? af.unit : 'MT'}
                      </td>
                      {/* Contract type */}
                      <td className="px-3 py-2.5">
                        {contractTypeBadge(af.contractType)}
                      </td>
                      {/* Hedge role */}
                      <td className="px-3 py-2.5">
                        {hedgeRoleCell(af)}
                      </td>
                      {/* Base material toggle */}
                      <td className="px-3 py-2.5 text-center">
                        <Tooltip title={af.isBaseMaterial ? 'Remove as base hedge material' : 'Set as base hedge material'}>
                          <Switch
                            size="small"
                            checked={af.isBaseMaterial}
                            onChange={() => handleToggleBase(af)}
                          />
                        </Tooltip>
                      </td>
                      {/* Price watch toggle */}
                      <td className="px-3 py-2.5 text-center">
                        <Tooltip title={isPW ? 'Show in all pages' : 'Watch only (historical prices)'}>
                          <Switch
                            size="small"
                            checked={isPW}
                            onChange={() => handleTogglePriceWatch(af)}
                          />
                        </Tooltip>
                      </td>
                      {/* Delete */}
                      <td className="px-3 py-2.5">
                        <Tooltip title="Remove from account">
                          <IconButton size="small" onClick={() => handleRemoveFormula(af)} className="!text-red-400 hover:!text-red-600">
                            <Delete className="w-4 h-4" />
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Material Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle className="!pb-2">
          <div className="flex items-center justify-between">
            <span>Add Material to Account</span>
            <IconButton size="small" onClick={() => setShowAddDialog(false)}>
              <Close />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="mb-4 mt-1">
            <TextField
              size="small"
              fullWidth
              placeholder="Search by code or name..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search className="w-4 h-4 text-gray-400 mr-2" />,
              }}
            />
          </div>

          <div className="max-h-80 overflow-y-auto border rounded-lg divide-y divide-gray-100">
            {availableFormulas.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400">
                {searchTerm ? 'No matching formulas' : 'All formulas already assigned'}
              </div>
            ) : (
              availableFormulas.map(f => (
                <div
                  key={f.id}
                  className="flex items-center justify-between px-4 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors"
                  onClick={() => handleAddFormula(f)}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm font-semibold text-gray-900 w-40">{f.code}</span>
                    <span className="text-sm text-gray-600">{f.name}</span>
                    {f.unit && <span className="text-xs text-gray-400">({f.unit})</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {contractTypeBadge(f.contractType)}
                    <Add className="w-4 h-4 text-blue-500" />
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MaterialsPerAccount;
